import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ChevronUp, ChevronDown, ClipboardList, Save } from "lucide-react";

export default function ChecklistsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [worksites, setWorksites] = useState([]);
  const [dimensionValues, setDimensionValues] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cl, ws, dims] = await Promise.all([
        api.get("/tenant/checklists"),
        api.get("/tenant/worksites"),
        api.get("/tenant/dimensions?active=true"),
      ]);
      setItems(cl.data.items);
      setWorksites(ws.data.items);
      const values = [];
      for (const d of dims.data.items) {
        const v = await api.get(`/tenant/dimensions/${d.id}/values?active=true`);
        v.data.items.forEach((x) => values.push({ ...x, dim_key: d.key, dim_label: d.label }));
      }
      setDimensionValues(values);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/tenant/checklists/${id}`);
      toast.success("Deactivated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-checklists-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Time admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Checklists</h1>
          <p className="mt-2 text-muted-foreground">Attach safety, sign-in, or setup checklists to clock-in flows. Items snapshot at clock-in so template edits won't rewrite history.</p>
        </div>
        <Button onClick={() => setEditing({ scope: "tenant", scope_id: null, name: "", items: [] })} data-testid="checklist-new-btn"><Plus className="h-4 w-4" /> New checklist</Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No checklists yet.</CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {items.map((cl) => (
            <Card key={cl.id} className="cursor-pointer hover:border-secondary/50" onClick={() => setEditing({ ...cl })} data-testid={`checklist-${cl.id}`}>
              <CardHeader>
                <CardTitle className="flex items-start justify-between gap-2 text-lg">
                  <span className="flex items-center gap-2"><ClipboardList className="h-4 w-4" /> {cl.name}</span>
                  <Badge variant={cl.active ? "default" : "secondary"}>{cl.active ? "Active" : "Archived"}</Badge>
                </CardTitle>
                <CardDescription>{cl.description || "—"}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="rounded-full text-[10px] uppercase">{cl.scope}</Badge>
                  {cl.scope !== "tenant" && (
                    <span className="text-xs text-muted-foreground">
                      {cl.scope === "worksite"
                        ? (worksites.find((w) => w.id === cl.scope_id)?.name || cl.scope_id)
                        : (dimensionValues.find((v) => v.id === cl.scope_id)?.label || cl.scope_id)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">{cl.items.length} item{cl.items.length === 1 ? "" : "s"} · {cl.items.filter((i) => i.required).length} required</div>
                <div className="mt-2 flex gap-1">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(cl.id); }} data-testid={`checklist-delete-${cl.id}`}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ChecklistEditorDialog
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
        worksites={worksites}
        dimensionValues={dimensionValues}
      />
    </div>
  );
}

function ChecklistEditorDialog({ editing, onClose, onSaved, worksites, dimensionValues }) {
  const [form, setForm] = useState({ name: "", description: "", scope: "tenant", scope_id: null, items: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || "",
        description: editing.description || "",
        scope: editing.scope || "tenant",
        scope_id: editing.scope_id || null,
        items: (editing.items || []).map((it, i) => ({ ...it, sort_order: i })),
      });
    }
  }, [editing]);

  if (!editing) return null;
  const isEdit = !!editing.id;

  const setItem = (i, patch) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });
  const addItem = () => setForm({ ...form, items: [...form.items, { label: "", required: false, sort_order: form.items.length }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const moveUp = (i) => { if (i === 0) return; const items = [...form.items]; [items[i - 1], items[i]] = [items[i], items[i - 1]]; setForm({ ...form, items }); };
  const moveDown = (i) => { if (i === form.items.length - 1) return; const items = [...form.items]; [items[i + 1], items[i]] = [items[i], items[i + 1]]; setForm({ ...form, items }); };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || undefined,
        scope: form.scope,
        scope_id: form.scope === "tenant" ? null : form.scope_id,
        items: form.items.filter((x) => x.label.trim()).map(({ label, required }) => ({ label, required: !!required })),
      };
      if (isEdit) await api.patch(`/tenant/checklists/${editing.id}`, payload);
      else await api.post("/tenant/checklists", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="checklist-editor-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit checklist" : "New checklist"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checklist-name" /></div>
            <div className="space-y-2"><Label>Scope</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, scope_id: null })}>
                <SelectTrigger data-testid="checklist-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant">Workspace-wide (all clock-ins)</SelectItem>
                  <SelectItem value="worksite">Worksite</SelectItem>
                  <SelectItem value="dimension_value">Dimension value</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.scope === "worksite" && (
            <div className="space-y-2"><Label>Worksite</Label>
              <Select value={form.scope_id || ""} onValueChange={(v) => setForm({ ...form, scope_id: v })}>
                <SelectTrigger data-testid="checklist-worksite"><SelectValue placeholder="Select worksite" /></SelectTrigger>
                <SelectContent>{worksites.map((w) => <SelectItem key={w.id} value={w.id}>{w.name} ({w.code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {form.scope === "dimension_value" && (
            <div className="space-y-2"><Label>Dimension value</Label>
              <Select value={form.scope_id || ""} onValueChange={(v) => setForm({ ...form, scope_id: v })}>
                <SelectTrigger data-testid="checklist-dimval"><SelectValue placeholder="Select value" /></SelectTrigger>
                <SelectContent>{dimensionValues.map((v) => <SelectItem key={v.id} value={v.id}>{v.dim_key}: {v.code} — {v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2"><Label>Description (optional)</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="checklist-description" /></div>

          <div className="rounded-xl border p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-medium">Items</div>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="checklist-item-add"><Plus className="h-3 w-3" /> Add</Button>
            </div>
            <div className="space-y-2">
              {form.items.length === 0 && <div className="text-center text-xs text-muted-foreground py-4">No items yet.</div>}
              {form.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2 rounded-md border p-2" data-testid={`checklist-item-${i}`}>
                  <div className="flex flex-col gap-1">
                    <Button size="icon" variant="ghost" onClick={() => moveUp(i)} className="h-6 w-6" data-testid={`checklist-item-up-${i}`}><ChevronUp className="h-3 w-3" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => moveDown(i)} className="h-6 w-6" data-testid={`checklist-item-down-${i}`}><ChevronDown className="h-3 w-3" /></Button>
                  </div>
                  <Input value={it.label} onChange={(e) => setItem(i, { label: e.target.value })} placeholder="Item label" data-testid={`checklist-item-label-${i}`} />
                  <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                    <Switch checked={!!it.required} onCheckedChange={(v) => setItem(i, { required: v })} data-testid={`checklist-item-req-${i}`} /> Required
                  </label>
                  <Button size="icon" variant="ghost" onClick={() => removeItem(i)} data-testid={`checklist-item-remove-${i}`}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.name.trim()} data-testid="checklist-save"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
